import { type EntityDescriptor } from '../entities';

import {
  type FlagTargetingAnalysis,
  type TargetingHandle,
} from './analyzeTargeting';

export type ToggleOperation = 'add' | 'remove';

/** One change that adds or removes the entity id in a flag's own rule. */
export interface ToggleWrite {
  type: 'flagClause';
  ruleId: string;
  clauseId: string;
  operation: ToggleOperation;
}

export type TogglePlan =
  | { kind: 'editable'; writes: ToggleWrite[] }
  | { kind: 'locked'; reason: string };

const MANAGED_ELSEWHERE = 'Managed by engineering.';

/**
 * Only a flag's own rules are written. Groups are shared by other flags, so
 * changing one here would change those flags too; individual targets have no
 * write here.
 */
const toWrite = (
  handle: TargetingHandle,
  operation: ToggleOperation
): ToggleWrite | undefined =>
  handle.type === 'flagClause'
    ? {
        type: 'flagClause',
        ruleId: handle.ruleId,
        clauseId: handle.clauseId,
        operation,
      }
    : undefined;

const planTurnOn = (
  analysis: FlagTargetingAnalysis,
  descriptor: EntityDescriptor
): TogglePlan => {
  if (analysis.handles.some((handle) => handle.present && !handle.servesOn)) {
    return {
      kind: 'locked',
      reason: `Turned off for this ${descriptor.label} by another rule.`,
    };
  }

  const servingOn = analysis.handles.filter(
    (handle) => handle.servesOn && !handle.present
  );
  const candidate = servingOn
    .map((handle) => toWrite(handle, 'add'))
    .find((write) => write !== undefined);

  if (!candidate) {
    return {
      kind: 'locked',
      reason:
        servingOn.length > 0
          ? MANAGED_ELSEWHERE
          : `Can't be turned on for a single ${descriptor.label}.`,
    };
  }

  return { kind: 'editable', writes: [candidate] };
};

const planTurnOff = (
  analysis: FlagTargetingAnalysis,
  descriptor: EntityDescriptor,
  reason?: string
): TogglePlan => {
  const listedOn = analysis.handles.filter(
    (handle) => handle.present && handle.servesOn
  );

  if (listedOn.length === 0) {
    return {
      kind: 'locked',
      reason:
        reason === 'FALLTHROUGH'
          ? `On for every ${descriptor.label} by default.`
          : `Turned on by a rule that covers more than this ${descriptor.label}.`,
    };
  }

  if (analysis.fallthroughIsOn) {
    return {
      kind: 'locked',
      reason: `Stays on: every ${descriptor.label} gets it by default.`,
    };
  }

  const writes = listedOn.map((handle) => toWrite(handle, 'remove'));
  if (writes.some((write) => write === undefined)) {
    return { kind: 'locked', reason: MANAGED_ELSEWHERE };
  }

  return {
    kind: 'editable',
    writes: writes.filter((write): write is ToggleWrite => Boolean(write)),
  };
};

/**
 * What flipping the switch writes, given whether the entity is served "on" now,
 * or why the switch can't change it. A plan only ever touches this entity:
 * switching a flag on or off for everyone is out of scope here.
 */
export const planToggle = ({
  analysis,
  descriptor,
  isOn,
  reason,
}: {
  analysis: FlagTargetingAnalysis;
  descriptor: EntityDescriptor;
  isOn: boolean;
  reason?: string;
}): TogglePlan => {
  if (!analysis.enabled) {
    return {
      kind: 'locked',
      reason: `Off for every ${descriptor.label} right now.`,
    };
  }

  return isOn
    ? planTurnOff(analysis, descriptor, reason)
    : planTurnOn(analysis, descriptor);
};
